import { RolesEnum, PublicRoutesEnum } from './enums';
import Page404 from '../pages/Page404/Page404';
import {
  useAppDispatch,
  useAppSelector,
  useAuth,
  useScrollTopOnRouteChange,
} from '../hooks';
import { IRoute } from './types';
import { useEffect, useState } from 'react';
import Login from '../pages/Login/Login';
import Register from '../pages/Register/Register';
import { Loader } from '../components/Loader/Loader';
import Routes from '../components/Routes';
import SellerRoutes from '../pages/Seller/SellerRoutes';
import { getAmazonConnectCredentials, getUser, getUserCompanies } from './api';
import { ACCESS_TOKEN_KEY, INITIAL_LD_KEY } from './constants';
import SetupCheckRoutes from '../pages/SetupCheck/SetupCheckRoutes';
import RegisterCustomer from '../pages/RegisterCustomer/RegisterCustomer';
import TermsOfService from '../pages/TermsOfService/TermsOfService';
import DataProcessingAgreement from '../pages/DataProcessingAgreement/DataProcessingAgreement';
import TaxAdvisorRoutes from '../pages/TaxAdvisor/TaxAdvisorRoutes';
import ForgotPassword from '../pages/ForgotPassword/ForgotPassword';
import CreatePassword from '../pages/CreatePassword/CreatePassword';
import Notifications from '../components/Notifications/Notifications';
import { useLDClient } from 'launchdarkly-react-client-sdk';

const docsRoutes = [
  {
    component: TermsOfService,
    path: PublicRoutesEnum.TermsOfService,
  },
  {
    component: DataProcessingAgreement,
    path: PublicRoutesEnum.DataProcessingAgreement,
  },
];

const error404Routes = [
  {
    component: Page404,
    path: PublicRoutesEnum.Page404,
  },
  {
    path: '*',
    redirectTo: PublicRoutesEnum.Page404,
  },
];

const redirectRoutes = [
  {
    path: PublicRoutesEnum.RegisterCustomer,
    redirectTo: '/',
  },
  {
    path: PublicRoutesEnum.Register,
    redirectTo: '/',
  },
];

const userRoutes: {
  [key: string]: IRoute[];
} = {
  [RolesEnum.SELLER]: [
    ...redirectRoutes,
    {
      component: SellerRoutes,
      path: '/',
    },
  ],
  [RolesEnum.SETUP_CHECK]: [
    ...redirectRoutes,
    {
      component: SetupCheckRoutes,
      path: '/',
    },
  ],
  [RolesEnum.TAX_ADVISOR]: [
    ...redirectRoutes,
    {
      component: TaxAdvisorRoutes,
      path: '/',
    },
  ],
};

const AppRoutes = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { authUser } = useAuth();
  const user = useAppSelector((state) => state.user.user);
  const ldClient = useLDClient();

  useEffect(() => {
    if (
      process.env.REACT_APP_ENV === 'prod' ||
      process.env.REACT_APP_ENV === 'stage' ||
      process.env.REACT_APP_ENV === 'development'
    ) {
      if (user) {
        ldClient?.identify(
          {
            key: user.Email,
          },
          '',
          () => {
            console.log('initialized', ldClient?.getContext());
          }
        );
      }

      if (!user && ldClient?.getContext().key !== INITIAL_LD_KEY) {
        ldClient?.identify(
          {
            key: INITIAL_LD_KEY,
          },
          '',
          () => {
            console.log('initialized', ldClient?.getContext());
          }
        );
      }
    }
    // eslint-disable-next-line
  }, [user]);

  useScrollTopOnRouteChange();

  const publicRoutes: IRoute[] = [
    {
      component: Login,
      path: '/',
      exact: true,
    },
    {
      component: ForgotPassword,
      path: PublicRoutesEnum.ForgotPassword,
    },
    {
      component: CreatePassword,
      path: PublicRoutesEnum.CreatePassword,
    },
    {
      component: RegisterCustomer,
      path: PublicRoutesEnum.RegisterCustomer,
    },
    {
      component: Register,
      path: PublicRoutesEnum.Register,
    },
    {
      path: '*',
      redirectTo: '/',
    },
  ];

  const routes = [
    ...docsRoutes,
    ...(user ? userRoutes[user.Role] || error404Routes : publicRoutes),
  ];

  useEffect(() => {
    if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
      Promise.all([
        getUser(),
        getAmazonConnectCredentials(),
        getUserCompanies(),
      ])
        .then((responses) => {
          authUser(responses);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [authUser, dispatch]);

  if (isLoading) return <Loader />;

  return (
    <>
      <Routes routes={routes} />
      <Notifications />
    </>
  );
};

export default AppRoutes;
